import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import sumBy from 'lodash-es/sumBy';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IBookingLineItem {
  booking_id: number;
  multi_unit_name: string;
  guest_name: string;
  channel_name: string;
  check_in: Date;
  check_out: Date;
  attributed_amounts: {
    number_of_nights: number;
    accommodation: number;
    extra_guest_charge: number;
    cleaning_fee: number;
    extra_charges: number;
    discount: number;
    channel_commission_with_tax: number;
    tax_withholding: number;
    management_fee: number;
    payment_processing_fee: number;
    booking_taxes: number;
    upsells: number;
    owner_payout: number;
  };
  total_amounts: {
    number_of_nights: number;
    accommodation: number;
    extra_guest_charge: number;
    cleaning_fee: number;
    extra_charges: number;
    discount: number;
    channel_commission_with_tax: number;
    tax_withholding: number;
    management_fee: number;
    payment_processing_fee: number;
    booking_taxes: number;
    upsells: number;
    owner_payout: number;
  };
}

export interface IPayoutSummary {
  channel_fees: number;
  expenses: number;
  other_fees: number;
  revenues: number;
  total: number;
}

export interface IInvoiceSummary {
  amount_due: number;
  expenses: number;
  management_fee: number;
  tax: number;
}

export default class ClientStatementReportModel extends Model {
  @attr('array') bookingLineItems!: IBookingLineItem[];
  @attr('object') payoutSummary?: IPayoutSummary;
  @attr('object') invoiceSummary?: IInvoiceSummary;

  @belongsTo('client-statement', {
    inverse: null,
    async: false,
  })
  clientStatement!: ModelRegistry['client-statement'];

  @cached
  get accommodationTotalSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.accommodation');
  }

  @cached
  get extraGuestChargeSum(): number {
    return sumBy(
      this.bookingLineItems,
      'attributed_amounts.extra_guest_charge',
    );
  }

  @cached
  get cleaningFeeSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.cleaning_fee');
  }

  @cached
  get extraChargesSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.extra_charges');
  }

  @cached
  get discountSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.discount');
  }

  @cached
  get channelCommissionSum(): number {
    return sumBy(
      this.bookingLineItems,
      'attributed_amounts.channel_commission_with_tax',
    );
  }

  @cached
  get taxWithholdingSum(): number {
    return (
      sumBy(this.bookingLineItems, 'attributed_amounts.tax_withholding') || 0
    );
  }

  @cached
  get managementFeeSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.management_fee');
  }

  @cached
  get paymentProcessingFeeSum(): number {
    return sumBy(
      this.bookingLineItems,
      'attributed_amounts.payment_processing_fee',
    );
  }

  @cached
  get bookingTaxesSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.booking_taxes');
  }

  @cached
  get ownerPayoutSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.owner_payout');
  }

  @cached
  get upsellsSum(): number {
    return sumBy(this.bookingLineItems, 'attributed_amounts.upsells');
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'client-statement-report': ClientStatementReportModel;
  }
}
